import { useState } from 'react';
import { Box, IconButton, Stack, Typography, Button, Grid2 } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { useMutation } from '@tanstack/react-query';
import EditIcon from '@mui/icons-material/Edit';
import CustomDivider from '../../shared/utils/CustomDivider';
import TableFilterTemplate from '../../shared/utils/TableFilterTemplate';
import { NormalContainerWithHeader } from '../../shared/components/student/NormalComponent';
import ToggleSwitch from '../../shared/utils/CustomToggle';
import { postLmsChapter } from '../../assets/nodeApi/postApi/postAdminApi';
import { putLmsChapters } from '../../assets/nodeApi/putApi/putAdminApi';
import { patchChapter } from '../../assets/nodeApi/patchApi/PatchAdminApi';
import { Grid_Gap, Input_Width } from '../../shared/constants';
import { useToaster } from '../../shared/utils/ToasterProvider';
import { TextFormField } from '../../shared/components/formfields/TextFormField';
import { queryClient } from '../../assets/nodeApi/reactQueryClient';
import { useLmsDropdownOptions } from './option/LsmOption';
import { IdAutoCompleteField } from '../../shared/components/formfields/IdAutoCompleteField';

const LmsChapter = () => {
    const { showToaster } = useToaster();
    const [selectedChapter, setSelectedChapter] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const { lmsSubject, lmsGrade, lmsChapter } = useLmsDropdownOptions(
        {
            gradeFilter: { status: true },
            subjectFilter: { status: "active" }
        }
    )

    const validationSchema = Yup.object().shape({
        subject_id: Yup.string().required('Subject is required'),
        grade_id: Yup.string().required('Grade is required'),
        name: Yup.string().required('Chapter is required'),
        description: Yup.string().required('Description is required'),

    });

    const initialValues = {
        subject_id: selectedChapter?.subjectId,
        grade_id: selectedChapter?.gradeId,
        name: selectedChapter?.name,
        description: selectedChapter?.description,
    };

    const mutationAdd = useMutation({
        mutationFn: (formData) => postLmsChapter(formData),
        onSuccess: () => {
            showToaster('Chapter added successfully!', '', 'success');
            queryClient.invalidateQueries(['chapters']);
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to add Chapter', '', 'error');
        },
    });


    const mutationUpdate = useMutation({
        mutationFn: ({ formData, id }) => putLmsChapters(formData, id),
        onSuccess: () => {
            showToaster('Chapter updated successfully!', '', 'success');
            queryClient.invalidateQueries(['chapters']);
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to update Chapter', '', 'error');
        },
    });


    const handleSubmit = (values, { resetForm }) => {

        if (isEditing) {
            mutationUpdate.mutate({ formData: values, id: selectedChapter?.id, });

        } else {
            mutationAdd.mutate(values);
        }
        resetForm({
            values: {
                subject_id: "",
                grade_id: "",
                name: "",
                description: ""
            }
        })
        setIsEditing(false);
        setSelectedChapter({});

    };

    const toggleStatusMutation = useMutation({
        mutationFn: ({ formData, id }) => patchChapter(formData, id),
        onSuccess: (response) => {
            showToaster(`Chapter ${response?.verify?.status ? "Enabled" : "Disabled"} successfully!`, '', 'success');
            queryClient.invalidateQueries(['chapters']);
        },
        onError: (err) => {
            showToaster(err?.response?.data?.error || 'Failed to update Chapter', '', 'error');
        },
    });

    const subjectColumns = [
        { field: 'sr_no', headerName: 'Sr No', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        { field: 'subject', headerName: 'Subject', flex: 1, renderCell: (params) => <Typography>{params.row?.subjects?.subject}</Typography> },
        { field: 'grade', headerName: 'Grade', flex: 1, renderCell: (params) => <Typography>{params.row?.lms_grades?.name}</Typography> },
        { field: 'name', headerName: 'Chapter Name', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        { field: 'description', headerName: 'Description', flex: 1, renderCell: (params) => <Typography>{params.value}</Typography> },
        {
            field: 'actionsEdit',
            headerName: 'Edit',
            flex: 1,
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    <IconButton
                        onClick={() => {
                            setSelectedChapter(params.row);
                            setIsEditing(true);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        size="small"
                    >
                        <EditIcon />
                    </IconButton>
                </Stack>
            ),
        },
        {
            field: 'actionsToggle',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => (
                <ToggleSwitch checked={params.row.status ? true : false}
                    onCheck={() =>
                        toggleStatusMutation.mutate({
                            formData: { status: !params.row.status ? true : false },
                            id: params.row.id
                        })
                    }
                />
            ),
        },
    ];

    return (
        <Box>
            <CustomDivider />
            <NormalContainerWithHeader headerText={isEditing ? 'Edit Chapter' : 'Add Chapter'}>
                <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
                    {({ resetForm }) => (
                        <Form>

                            <Grid2 container spacing={Grid_Gap}>

                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="grade_id"
                                        component={IdAutoCompleteField}
                                        label="Select Grade"
                                        options={lmsGrade.options ?? []}
                                        isLoading={lmsGrade.isLoading}
                                        isRequired
                                    />
                                </Grid2>

                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="subject_id"
                                        component={IdAutoCompleteField}
                                        options={lmsSubject.options}
                                        isLoading={lmsSubject.isLoading}
                                        label="Select Subject"
                                        isRequired
                                    />
                                </Grid2>

                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="name"
                                        component={TextFormField}
                                        label="Chapter"
                                        isRequired
                                    />
                                </Grid2>

                                <Grid2 children size={Input_Width}>
                                    <Field
                                        name="description"
                                        component={TextFormField}
                                        label="Description"
                                        isRequired
                                    />
                                </Grid2>

                            </Grid2>

                            <CustomDivider />

                            <Grid2 container spacing={Grid_Gap} sx={{ justifyContent: "end" }}>
                                <Grid2 children size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Button
                                        variant="outlined"
                                        type="button"
                                        onClick={() => {
                                            resetForm({
                                                values: {
                                                    subject_id: "",
                                                    grade_id: "",
                                                    name: "",
                                                    description: ""
                                                }
                                            })
                                            setIsEditing(false);
                                            setSelectedChapter(null);
                                        }}
                                        fullWidth
                                    >
                                        {isEditing ? 'Cancel' : 'Reset'}
                                    </Button>
                                </Grid2>
                                <Grid2 children size={{ xs: 12, sm: 6, md: 3 }}>
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        fullWidth
                                        disabled={mutationAdd.isPending || mutationUpdate.isPending}
                                    >
                                        {mutationUpdate.isPending
                                            ? "Updating..."
                                            : mutationAdd.isPending
                                                ? "Submitting..."
                                                : isEditing
                                                    ? "Update"
                                                    : "Submit"}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        </Form>
                    )}
                </Formik>
            </NormalContainerWithHeader>

            <CustomDivider />

            <TableFilterTemplate
                title="Chapter List"
                columns={subjectColumns}
                rowsData={lmsChapter.options || []}
                isLoading={lmsChapter.isLoading}
            />

        </Box>
    );
};

export default LmsChapter;






